/**
 * PDF Data Hook
 * Fetches session data for PDF generation
 */

import { useState, useEffect, useCallback } from 'react';
import { timeInfoApi, zeitschritteApi, csvFilesApi } from '@/api';
import { supabase } from '@/api/client/supabaseClient';
import { SessionData } from '../types';

interface UsePDFDataResult {
  sessionData: SessionData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches session name from Supabase via session_mappings
 */
async function fetchSessionName(stringSessionId: string): Promise<string | null> {
  try {
    // First get the UUID from session_mappings
    const { data: mappingData, error: mappingError } = await supabase
      .from('session_mappings')
      .select('uuid_session_id')
      .eq('string_session_id', stringSessionId)
      .single();

    if (mappingError || !mappingData?.uuid_session_id) {
      console.warn('Could not fetch session mapping:', mappingError?.message);
      return null;
    }

    // Then fetch session name from sessions table
    const { data: sessionData, error: sessionError } = await supabase
      .from('sessions')
      .select('session_name')
      .eq('id', mappingData.uuid_session_id)
      .single();

    if (sessionError || !sessionData) {
      console.warn('Could not fetch session name:', sessionError?.message);
      return null;
    }

    return sessionData.session_name || null;
  } catch (err) {
    console.error('Error fetching session name:', err);
    return null;
  }
}

export function usePDFData(sessionId: string): UsePDFDataResult {
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSessionData = useCallback(async () => {
    if (!sessionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [timeInfo, zeitschritte, files, sessionName] = await Promise.all([
        timeInfoApi.getTimeInfo(sessionId).catch(() => null),
        zeitschritteApi.getZeitschritte(sessionId).catch(() => null),
        csvFilesApi.getCSVFiles(sessionId).catch(() => []),
        fetchSessionName(sessionId),
      ]);

      setSessionData({
        timeInfo: timeInfo as SessionData['timeInfo'],
        zeitschritte: zeitschritte as SessionData['zeitschritte'],
        files: files as SessionData['files'],
        sessionName,
      });
    } catch (err) {
      console.error('Error fetching PDF data:', err);
      setError('Greška pri dohvaćanju podataka za PDF');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSessionData();
  }, [loadSessionData]);

  return {
    sessionData,
    isLoading,
    error,
    refetch: loadSessionData,
  };
}
